import { useRef, useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { flushSync } from 'react-dom';
import { TranslationService } from '../../../services/translation.service';
import { SanitizerService } from '../../../services/sanitizer.service';
import WizardStep from '../../Shared/WizardStep';
import { WizardStepState } from '../../Shared/WizardStep/models/wizard-step-state.enum';
import ValidationSummary from '../../Shared/ValidationSummary';
import TripLegSelector, { type ITripLegSelectorState } from '../../Shared/TripLegSelector';
import ValidationInfo from '../../../validation/validation-info';
import type { IJourney } from '../../../models/journey.model';
import type { ITripLeg } from '../../../models/trip-leg.model';
import { AnalyticsService } from '../../../services/analytics-service';
import type IValidatable from '../../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../../helpers/validation.helper';
import ContactInformationLookup, { type IContactInformationLookupState } from '../../Shared/ContactInformationLookup';
import { ContactInformationLookupField } from '../../Shared/ContactInformationLookup/contact-information-lookup-field.enum';
import HoneyPot, { type IHoneyPot } from '../../Shared/HoneyPot';
import CompensationTypeSelector, { type ICompensationTypeSelectorState } from '../../Shared/CompensationTypeSelector';
import { CompensationType } from '../../../models/compensation-type.enum';
import type { IConsentState } from '../../Shared/Consent';
import Consent from '../../Shared/Consent';
import PaymentMethodSelector, { type IPaymentMethodSelectorState } from '../../Shared/PaymentMethodSelector';
import { PaymentMethodSelectorField } from '../../Shared/PaymentMethodSelector/payment-method-selector-field.enum';
import { PaymentMethod } from '../../../models/payment-method.enum';
import { Status } from '../../../hooks/fetch.hook';
import { LanguageService } from '../../../services/language.service';
import SubmitFailed from '../../Shared/SubmitFailed';
import SubmitSuccessful from '../../Shared/SubmitSuccessful';
import { emptyContactInformation } from '../../../models/contact-information.model';
import type { IServerValidationError } from '../../Shared/validatable.interface';
import { ClientSideStorageItems } from '../../../services/client-side-storage.service';
import Sign from '../components/Sign';
import { ConfigurationService } from '../../../services/configuration.service';
import { DomHelper } from '../../../helpers/dom.helper';
import { useTexts } from '../../../hooks/texts.hook';
import TransportWarrantyActualArrivalSelector, { ITransportWarrantyActualArrivalSelectorState } from './TransportWarrantyActualArrivalSelector';
import IDelayCompensationTransportWarrantyApplication from './models/delay-compensation-transport-warranty-application.model';
import { ReplacementTransportMode } from './TransportWarrantyActualArrivalSelector/components/ReplacementTransport/replacement-transport-mode.enum';
import { TransportWarrantyActualArrivalSelectorMode } from './TransportWarrantyActualArrivalSelector/transport-warranty-actual-arrival-selector.enum';
import AntiforgeryService from '../../../services/anti-forgery.service';
import { PersonalIdentityNumberFeature } from '../../Shared/PersonalIdentityNumberLookup/personal-identity-lookup-feature.enum';

function DelayCompensationTransportWarranty() {
  // === CONFIGURATION ===
  const enableBankIdFeatureFlag = ConfigurationService.get('delaycompensationpagetransportwarrantypage.enableFeatureFlag') === 'true';
  const { t } = useTexts();

  // === STATE ===
  const [step1State, setStep1State] = useState(WizardStepState.active);
  const [step2State, setStep2State] = useState(WizardStepState.inactive);
  const [step3State, setStep3State] = useState(WizardStepState.inactive);
  const [, setStep2Confirmed] = useState(false);
  const [tripLegSelectorState, setTripLegSelectorState] = useState<ITripLegSelectorState>({
    value: { journey: undefined, tripLeg: undefined, tripLegIndex: undefined },
    validationInfo: new ValidationInfo(),
  });
  const [actualArrivalSelectorState, setActualArrivalSelectorState] = useState<ITransportWarrantyActualArrivalSelectorState>({
    value: {
      mode: TransportWarrantyActualArrivalSelectorMode.edit,
      comments: '',
      transportMode: ReplacementTransportMode.none,
      from: '',
      to: '',
      taxiSum: undefined,
      registrationNumber: '',
      distance: undefined,
      parkingFee: undefined,
      congestionTax: undefined,
      receiptSum: undefined,
      otherTransportType: '',
      files: [],
    },
    validationInfo: new ValidationInfo(),
  });
  const [contactInformationLookupState, setContactInformationLookupState] = useState<IContactInformationLookupState>({
    value: emptyContactInformation,
    validationInfo: new ValidationInfo(),
  });
  const [paymentMethodSelectorState, setPaymentMethodSelectorState] = useState<IPaymentMethodSelectorState>({
    value: { paymentMethod: PaymentMethod.ToGo, travelCardNumber: '', toGoTicketNumber: '', otherTicketType: '' },
    validationInfo: new ValidationInfo(),
  });
  const [compensationTypeSelectorState, setCompensationTypeSelectorState] = useState<ICompensationTypeSelectorState>({
    value: CompensationType.ToGoVoucherByEmail,
    validationInfo: new ValidationInfo(),
  });
  const [consentState, setConsentState] = useState<IConsentState>({ value: false, validationInfo: new ValidationInfo() });
  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [submitErrorCode, setSubmitErrorCode] = useState<number | undefined>(undefined);
  const [showSign, setShowSign] = useState(false);

  // === REFS ===
  const actualArrivalSelectorRef = useRef<IValidatable>(null!);
  const step2Ref = useRef<HTMLDivElement>(null!);
  const contactInformationLookupRef = useRef<IValidatable>(null!);
  const paymentMethodSelectorRef = useRef<IValidatable>(null!);
  const compensationTypeSelectorRef = useRef<IValidatable>(null!);
  const consentRef = useRef<IValidatable>(null!);
  const honeyPotRef = useRef<IHoneyPot>(null!);

  // === EFFECTS ===
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setConsentState({ value: false, validationInfo: new ValidationInfo() });
  }, [tripLegSelectorState.value, actualArrivalSelectorState.value, contactInformationLookupState.value, paymentMethodSelectorState.value, compensationTypeSelectorState.value]);

  // === HELPER FUNCTIONS ===

  function getValidatableComponentsInStep2(): ValidatableComponent[] {
    return [{ component: actualArrivalSelectorRef.current, validationInfo: actualArrivalSelectorState.validationInfo, setState: setActualArrivalSelectorState }];
  }

  function getValidatableComponentsInStep3(): ValidatableComponent[] {
    return [
      { component: contactInformationLookupRef.current, validationInfo: contactInformationLookupState.validationInfo, setState: setContactInformationLookupState },
      { component: paymentMethodSelectorRef.current, validationInfo: paymentMethodSelectorState.validationInfo, setState: setPaymentMethodSelectorState },
      { component: compensationTypeSelectorRef.current, validationInfo: compensationTypeSelectorState.validationInfo, setState: setCompensationTypeSelectorState },
      { component: consentRef.current, validationInfo: consentState.validationInfo, setState: setConsentState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): number | undefined {
    const invalidValidationInfosInStep2 = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponentsInStep2());
    if (invalidValidationInfosInStep2.length > 0) {
      setStep2State(WizardStepState.active);
      setActualArrivalSelectorState((ps) => ({ ...ps, value: { ...ps.value, mode: TransportWarrantyActualArrivalSelectorMode.edit } }));
      ValidationHelper.trackValidationErrors(invalidValidationInfosInStep2, (e) => {
        AnalyticsService.addEvent(AnalyticsService.DelayCompensationTransportWarrantyCategory, AnalyticsService.ErrorAction, e);
      });
      return 2;
    }

    const invalidValidationInfosInStep3 = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponentsInStep3());
    if (invalidValidationInfosInStep3.length > 0) {
      setStep3State(WizardStepState.active);
      ValidationHelper.trackValidationErrors(invalidValidationInfosInStep3, (e) => {
        AnalyticsService.addEvent(AnalyticsService.DelayCompensationTransportWarrantyCategory, AnalyticsService.ErrorAction, e);
      });
      return 3;
    }

    return undefined;
  }

  // === VARIABLES ===
  const step1IsCollapsed = step1State === WizardStepState.inactive || step1State === WizardStepState.unknown;
  const step2IsCollapsed = step2State === WizardStepState.inactive || step2State === WizardStepState.unknown;
  const step3IsCollapsed = step3State === WizardStepState.inactive || step3State === WizardStepState.unknown;
  const validationSummaryErrorsStep2: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponentsInStep2()).filter((e) => e.length > 0);
  const validationSummaryErrorsStep3: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponentsInStep3()).filter((e) => e.length > 0);
  const wizardIsComplete = step1State === WizardStepState.completed && step2State === WizardStepState.completed && step3State === WizardStepState.completed;

  // === EVENT HANDLERS ===
  function handleStep1Select(value: { journey: IJourney | undefined; tripLeg: ITripLeg | undefined; tripLegIndex: number | undefined }) {
    flushSync(() => {
      setTripLegSelectorState((ps) => ({ ...ps, value }));
      setActualArrivalSelectorState((ps) => ({
        ...ps,
        value: {
          ...ps.value,
          mode: TransportWarrantyActualArrivalSelectorMode.edit,
        },
        validationInfo: new ValidationInfo(),
      }));
      setStep1State(WizardStepState.completed);
      setStep2State(WizardStepState.active);
      AnalyticsService.addEvent(AnalyticsService.DelayCompensationTransportWarrantyCategory, 'Klick på checkbox i reseväljare', '');
    });
    DomHelper.scrollAndFocus('delay-compensation-form-step-2-heading');
  }

  function handleStep1EditButtonClick() {
    setStep3State(WizardStepState.inactive);
  }

  function handleStep2NextStepButtonClick(): void {
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    flushSync(() => {
      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponentsInStep2());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.DelayCompensationTransportWarrantyCategory, AnalyticsService.ErrorAction, e);
        });
        return;
      }
      setActualArrivalSelectorState((ps) => ({ ...ps, value: { ...ps.value, mode: TransportWarrantyActualArrivalSelectorMode.summary }, validationInfo: new ValidationInfo() }));
      setStep2State(WizardStepState.completed);
      setStep3State(WizardStepState.active);
      setStep2Confirmed(true);
      AnalyticsService.addEvent(AnalyticsService.DelayCompensationTransportWarrantyCategory, 'Klick på klarknapp steg 2', '');
    });
    if (isFormValid) {
      DomHelper.scrollAndFocus('delay-compensation-form-step-3-heading');
    } else {
      const step2 = document.getElementById('delay-compensation-form-step-2');
      if (step2) {
        step2.scrollIntoView();
        (step2.getElementsByClassName('validation-summary')[0] as HTMLElement)?.focus();
      }
    }
  }

  function handleStep2EditButtonClick(): void {
    flushSync(() => {
      setActualArrivalSelectorState((ps) => ({ ...ps, value: { ...ps.value, mode: TransportWarrantyActualArrivalSelectorMode.edit } }));
      setStep2State(WizardStepState.active);
      setStep3State(WizardStepState.inactive);
      AnalyticsService.addEvent(AnalyticsService.DelayCompensationTransportWarrantyCategory, 'Klick på ändra i steg 2', '');
    });
    const step2 = document.getElementById('delay-compensation-form-step-2');
    if (step2) {
      step2.scrollIntoView();
      DomHelper.focusOnFirstFocusableElement(step2);
    }
  }

  async function submitForm(
    validateAndShowBankid: boolean,
    orderRef?: string | undefined | null,
    transactionId?: string | undefined | null,
  ): Promise<{ status: Status; errorCode: number | undefined; stepNumber: number | undefined }> {
    setSubmitStatus(Status.loading);
    const application: IDelayCompensationTransportWarrantyApplication = {
      plannedJourneyDetailsReference: tripLegSelectorState.value.journey?.detailsReference,
      plannedJourneyBrokenTripLegIndex: tripLegSelectorState.value.tripLegIndex,
      plannedServiceJourneyGid: tripLegSelectorState.value.tripLeg?.serviceJourney.gid,
      actualArrivalTransportMode: actualArrivalSelectorState.value.transportMode,
      actualArrivalFrom: actualArrivalSelectorState.value.from,
      actualArrivalTo: actualArrivalSelectorState.value.to,
      actualArrivalTaxiSumInSek: actualArrivalSelectorState.value.taxiSum,
      actualArrivalRegistrationNumber: actualArrivalSelectorState.value.registrationNumber,
      actualArrivalDistanceInKm: actualArrivalSelectorState.value.distance,
      actualArrivalParkingFeeInSek: actualArrivalSelectorState.value.parkingFee,
      actualArrivalCongestionTaxInSek: actualArrivalSelectorState.value.congestionTax,
      actualArrivalOtherTransportType: actualArrivalSelectorState.value.otherTransportType,
      actualArrivalReceiptSumInSek: actualArrivalSelectorState.value.receiptSum,
      actualArrivalComments: actualArrivalSelectorState.value.comments,
      personalIdentityNumber: contactInformationLookupState.value.personalIdentityNumber,
      phoneNumber: contactInformationLookupState.value.phoneNumber,
      email: contactInformationLookupState.value.email,
      paymentMethodType: paymentMethodSelectorState.value.paymentMethod,
      paymentMethodToGoTicketNumber: paymentMethodSelectorState.value.toGoTicketNumber,
      paymentMethodTravelCardNumber: paymentMethodSelectorState.value.travelCardNumber,
      paymentMethodOtherTicketType: paymentMethodSelectorState.value.otherTicketType,
      compensationType: compensationTypeSelectorState.value,
      isConsentGiven: consentState.value,
      orderRef: orderRef ?? '',
      transactionId: transactionId ?? '',
      formCreatedDateTime: DateTime.now(),
    };

    const formData = new FormData();
    formData.append('delayCompensationApplication', JSON.stringify(application));
    actualArrivalSelectorState.value.files.forEach((file, index) => {
      formData.append(`files[${index}]`, file.actualFile);
    });

    try {
      const response = await fetch(
        `/api/${validateAndShowBankid ? 'validatedelaycompensationtransportwarranty' : 'delaycompensationtransportwarranty'}?language=${LanguageService.getCurrentLanguage()}`,
        {
          method: 'POST',
          body: formData,
          headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        },
      );
      if (response.ok) {
        if (validateAndShowBankid) {
          setShowSign(true);
          return { status: Status.loading, errorCode: undefined, stepNumber: undefined };
        }
        setSubmitStatus(Status.success);
        AnalyticsService.addEvent(AnalyticsService.DelayCompensationTransportWarrantyCategory, 'Lyckad formulärinskickning', '');
        return { status: Status.success, errorCode: undefined, stepNumber: undefined };
      }

      setSubmitStatus(Status.failed);
      setSubmitErrorCode(response.status);
      AnalyticsService.addEvent(AnalyticsService.DelayCompensationTransportWarrantyCategory, 'Misslyckad formulärinskickning', '');
      if (response.status === 400) {
        const serverValidationErrors = (await response.json()).errors as IServerValidationError[];
        if (serverValidationErrors?.length > 0) {
          const stepNumber = handleServerValidationErrors(serverValidationErrors);
          return { status: Status.failed, errorCode: 400, stepNumber };
        }
        // No server validation errors, treat as 500 and show generic error message
        setSubmitErrorCode(500);
        return { status: Status.failed, errorCode: 500, stepNumber: undefined };
      }
      return { status: Status.failed, errorCode: undefined, stepNumber: undefined };
    } catch {
      setSubmitStatus(Status.failed);
      return { status: Status.failed, errorCode: undefined, stepNumber: undefined };
    }
  }

  async function handleSubmitClick(useBankId: boolean): Promise<void> {
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined; stepNumber: number | undefined } | undefined;
    await flushSync(async () => {
      if (enableBankIdFeatureFlag && !useBankId) {
        return;
      }

      if (submitStatus === Status.loading) return;

      setSubmitStatus(Status.notperformed);
      setSubmitErrorCode(undefined);

      if (!useBankId && !honeyPotRef.current.isValid()) return;

      AnalyticsService.addEvent(AnalyticsService.DelayCompensationTransportWarrantyCategory, 'Klick på skicka in formulärknapp', '');
      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponentsInStep3());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(
            AnalyticsService.DelayCompensationTransportWarrantyCategory,
            AnalyticsService.ErrorAction,
            `${TranslationService.get('delaycompensationformpage.stepthreeheading')} - ${e}`,
          );
        });
        return;
      }

      setSubmitStatus(Status.loading);
      submitResult = await submitForm(useBankId);
      if (!useBankId && submitResult.errorCode !== 400) {
        setStep3State(WizardStepState.completed);
      }
    });
    if (!isFormValid || submitResult?.errorCode === 400) {
      const step = document.getElementById(`delay-compensation-form-step-${submitResult?.stepNumber || 3}`);
      if (step) {
        step.scrollIntoView();
        (step.getElementsByClassName('validation-summary')[0] as HTMLElement)?.focus();
      }
    } else if (!useBankId) {
      setTimeout(() => {
        DomHelper.scrollAndFocus('delaycompensationTransportWarrantyForm');
      }, 100);
    }
  }

  async function handleSignComplete(success: boolean, transactionId: string | null, orderRef: string | null) {
    await flushSync(async () => {
      setShowSign(false);
      if (success) {
        await submitForm(false, orderRef, transactionId);
        setStep3State(WizardStepState.completed);
      } else {
        setSubmitStatus(Status.notperformed);
      }
    });
    if (success) {
      DomHelper.scrollAndFocus('delaycompensationTransportWarrantyForm');
    } else {
      DomHelper.scrollAndFocus('sign-button');
    }
  }

  // === RENDER ===
  return (
    <div className="delay-compensation-form">
      <div className={!wizardIsComplete && submitErrorCode !== 500 ? '' : 'd-none'}>
        <div className="mb-4 mb-md-5" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('delaycompensationformpage.textbody')) }}></div>
        <WizardStep id="delay-compensation-form-step-1" stepLabel={t('Steg', 'Step')} number={1} state={step1State} heading={TranslationService.get('delaycompensationformpage.steponeheading')} className="mb-3 mb-md-4">
          <div className={`p-3 ${step1IsCollapsed ? 'd-none' : ''}`}>
            {TranslationService.get('delaycompensationformpage.steponedescription') && (
              <div className="mb-3" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('delaycompensationformpage.steponedescription')) }}></div>
            )}
            {step1State === WizardStepState.active && <p>{TranslationService.get('common.formsrequiredfieldsmessage')}</p>}
            <TripLegSelector
              id="delay-compensation-trip-leg-selector"
              selectLegText={TranslationService.get('delaycompensationformpage.selectplannedtriptext')}
              onlyHistoricTrips={true}
              localStorageItemFrom={ClientSideStorageItems.LocalStorage.AutocompleteHistoryDelayCompensationFrom}
              localStorageItemTo={ClientSideStorageItems.LocalStorage.AutocompleteHistoryDelayCompensationTo}
              state={tripLegSelectorState}
              onChange={handleStep1Select}
              onEditButtonClick={handleStep1EditButtonClick}
            />
          </div>
        </WizardStep>

        <WizardStep id="delay-compensation-form-step-2" stepLabel={t('Steg', 'Step')} headingId="delay-compensation-form-step-2-heading" number={2} state={step2State} heading={TranslationService.get('delaycompensationformpage.steptwoheading')} className="mb-3 mb-md-4">
          <div className={`p-3 ${step2IsCollapsed ? 'd-none' : ''}`} ref={step2Ref}>
            {step2State === WizardStepState.active && <p>{TranslationService.get('common.formsrequiredfieldsmessage')}</p>}
            {step2State === WizardStepState.active && <ValidationSummary validationSummaryErrors={validationSummaryErrorsStep2} />}
            <TransportWarrantyActualArrivalSelector
              ref={actualArrivalSelectorRef}
              className={step2State === WizardStepState.active ? 'mb-3' : ''}
              state={actualArrivalSelectorState}
              onChange={(value) => {
                setActualArrivalSelectorState((ps) => ({ ...ps, value }));
              }}
              onKeyUp={(e) => {
                setActualArrivalSelectorState((ps) => {
                  const newValidationInfo = new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children });
                  newValidationInfo.setChild(e, new ValidationInfo());
                  return { ...ps, validationInfo: newValidationInfo };
                });
              }}
              onEditClick={handleStep2EditButtonClick}
            />
            {step2State === WizardStepState.active && (
              <div className="d-grid">
                <button className="btn btn-primary" onClick={handleStep2NextStepButtonClick}>
                  {TranslationService.get('delaycompensationformpage.nextstep')}
                </button>
              </div>
            )}
          </div>
        </WizardStep>

        <WizardStep id="delay-compensation-form-step-3" stepLabel={t('Steg', 'Step')} headingId="delay-compensation-form-step-3-heading" number={3} state={step3State} heading={TranslationService.get('delaycompensationformpage.stepthreeheading')} className="">
          <div className={`p-3 ${step3IsCollapsed ? 'd-none' : ''}`}>
            <div>
              <ValidationSummary validationSummaryErrors={validationSummaryErrorsStep3} />
              <p>{TranslationService.get('common.formsrequiredfieldsmessage')}</p>
              <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('delaycompensationformpage.contactinformationtext')) }}></div>
              <ContactInformationLookup
                ref={contactInformationLookupRef}
                state={contactInformationLookupState}
                feature={PersonalIdentityNumberFeature.DelayCompensation}
                onChange={(value) => {
                  setContactInformationLookupState((ps) => ({ ...ps, value }));
                }}
                onKeyUp={(e) => {
                  if (e === ContactInformationLookupField.personalIdentityNumber) {
                    setContactInformationLookupState((ps) => ({
                      ...ps,
                      validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, personalIdentityNumber: new ValidationInfo() }),
                    }));
                  } else if (e === ContactInformationLookupField.phoneNumber) {
                    setContactInformationLookupState((ps) => ({
                      ...ps,
                      validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                        ...ps.validationInfo.children,
                        phoneNumber: new ValidationInfo(),
                      }),
                    }));
                  } else if (e === ContactInformationLookupField.email) {
                    setContactInformationLookupState((ps) => ({
                      ...ps,
                      validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                        ...ps.validationInfo.children,
                        email: new ValidationInfo(),
                      }),
                    }));
                  }
                }}
              />
            </div>
            <div className={contactInformationLookupState.value.personalIdentityNumber ? '' : 'd-none'}>
              <hr className="my-4" />
              <PaymentMethodSelector
                ref={paymentMethodSelectorRef}
                state={paymentMethodSelectorState}
                heading={TranslationService.get('delaycompensatiotransportwarrantyformpage.paymentmethodheading')}
                onChange={(paymentMethod) => setPaymentMethodSelectorState((ps) => ({ ...ps, value: paymentMethod }))}
                onKeyUp={(e) => {
                  if (e === PaymentMethodSelectorField.paymentMethod) {
                    setPaymentMethodSelectorState((ps) => ({
                      ...ps,
                      validationInfo: new ValidationInfo(),
                    }));
                  } else if (e === PaymentMethodSelectorField.toGoTicketNumber) {
                    setPaymentMethodSelectorState((ps) => ({
                      ...ps,
                      validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, toGoTicketNumber: new ValidationInfo() }),
                    }));
                  } else if (e === PaymentMethodSelectorField.travelCardNumber) {
                    setPaymentMethodSelectorState((ps) => ({
                      ...ps,
                      validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, travelCardNumber: new ValidationInfo() }),
                    }));
                  } else if (e === PaymentMethodSelectorField.otherTicketType) {
                    setPaymentMethodSelectorState((ps) => ({
                      ...ps,
                      validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, otherTicketType: new ValidationInfo() }),
                    }));
                  }
                }}
              />
              <hr className="my-4" />
              <CompensationTypeSelector
                ref={compensationTypeSelectorRef}
                translationBase="delaycompensatiotransportwarrantyformpage.compensationtype"
                availableCompensationTypes={[CompensationType.ToGoVoucherByEmail, CompensationType.BankAccount]}
                state={compensationTypeSelectorState}
                additionalInfo={[{ compensationType: CompensationType.ToGoVoucherByEmail, additionalInfo: TranslationService.get('delaycompensatiotransportwarrantyformpage.voucherinfo') }]}
                onChange={(compensationType) => setCompensationTypeSelectorState((ps) => ({ ...ps, value: compensationType }))}
              />
              <hr className="my-4" />
              <Consent
                id="delay-compensation-consent"
                ref={consentRef}
                consentText={TranslationService.get('customerserviceforms.consenttext')}
                integrityPolicyInfo={TranslationService.get('customerserviceforms.integritypolicyinfo')}
                termsAndConditions={TranslationService.get('customerserviceforms.termsandconditions.heading')}
                state={consentState}
                className={enableBankIdFeatureFlag ? 'mb-4' : ''}
                onChange={(value) => {
                  setConsentState({ value, validationInfo: new ValidationInfo() });
                }}
              />
              {enableBankIdFeatureFlag && (
                <>
                  <div className="d-grid">
                    <button id="sign-button" type="button" className={`btn btn-primary btn--bankid ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={() => handleSubmitClick(true)}>
                      {TranslationService.get('delaycompensationformpage.signwithbankid')}
                    </button>
                  </div>
                  <Sign
                    show={showSign}
                    signComplete={handleSignComplete}
                    contentGuid={ConfigurationService.get('delaycompensationpagetransportwarrantypage.guid')!}
                    personalIdentityNumber={contactInformationLookupState.value.personalIdentityNumber}
                  ></Sign>
                </>
              )}
              {!enableBankIdFeatureFlag && (
                <>
                  <hr className="my-4" />
                  <HoneyPot ref={honeyPotRef} />
                  <div className="d-grid">
                    <button
                      className={`btn btn-primary
                    ${submitStatus === Status.loading ? 'btn-spinner' : ''}`}
                      onClick={() => handleSubmitClick(false)}
                    >
                      {TranslationService.get('delaycompensationformpage.submitapplication')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </WizardStep>
      </div>
      <div id="wizard-completed-result" role="alert" aria-live="polite" tabIndex={-1}>
        {submitStatus === Status.success && <SubmitSuccessful successText={TranslationService.get('delaycompensationformpage.completedtext')} />}
        {submitStatus === Status.failed && submitErrorCode !== 400 && (
          <>
            <SubmitFailed failedText={TranslationService.get('delaycompensationformpage.generalerrortext')} />
            {submitErrorCode === 422 ? (
              <button
                className="btn btn-primary d-block mx-auto"
                onClick={() => {
                  window.location.reload();
                }}
              >
                {TranslationService.get('delaycompensationformpage.reloadpage')}
              </button>
            ) : (
              <button
                className="btn btn-primary d-block mx-auto"
                onClick={() => {
                  setSubmitStatus(Status.notperformed);
                  setSubmitErrorCode(undefined);
                  setStep3State(WizardStepState.active);
                }}
              >
                {TranslationService.get('delaycompensationformpage.dismissgeneralerror')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DelayCompensationTransportWarranty;
